import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { useTranslation } from 'next-i18next/pages'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Center,
  Text,
  Icon,
  Box,
  Flex,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { Spinner } from '@/components/atoms/Spinner'
import { EXPLORERS } from '@/constants/constants'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { GET_COINFLIP_GAME } from '@/api/query'
import { CoinflipChoice, CoinflipGameMode, Get_Coinflip_GameQuery, MiniGameStatus } from '@/__generated__/graphql'
import { useEffect } from 'react'
import { UserGameResult } from '../UserGameResult/UserGameResult'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { formatAmount } from '@/helpers/formatAmount'
import CoinHeadIcon from '@/public/assets/images/coinflip/coin-head.svg'
import CoinTailIcon from '@/public/assets/images/coinflip/coin-tail.svg'
import { StepBadge, StepCoinBadge } from '@/components/templates/Coinflip/StepBadge'
import { calculateMultipliers } from '@/components/templates/Coinflip/CoinflipProvider'
import { BigNumber } from '@injectivelabs/utils'
import { getTokenByDenom } from '@/services/token-service'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'

export const CoinflipGameDetails = ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation()

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.game-details', { gameId })}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <CoinflipGame gameId={gameId} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const CoinflipGame = ({ gameId }: { gameId: string }) => {
  const { t } = useTranslation()
  const { data, error, refetch } = useQuery(GET_COINFLIP_GAME, {
    variables: { id: gameId },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (error) {
    return <Text color={'ninja.200'}>{t('common.error-occurred')}</Text>
  }

  if (!data) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const gameDetails = transformCoinflipGameDetails(data)
  const token = getTokenByDenom(gameDetails.denom!)

  return (
    <>
      <UserGameResult
        address={gameDetails.player!}
        multiplier={(parseInt(gameDetails.payable) / parseInt(gameDetails.bet)).toFixed(2)}
        payout={{ amount: gameDetails.payable, denom: gameDetails.denom! }}
        result={gameDetails.status!}
      />
      <SimpleGrid columnGap={3} columns={{ base: 2, sm: 2 }}>
        <StatsRow name={t('common.game-status')} value={<Text color={'brand.green'}>{t('common.finished')}</Text>} />
        <StatsRow
          name={t('common.bet')}
          value={
            <FiatOrCryptoAmount
              value={new TokenMoney(gameDetails.bet, token.denom)}
              denom={token.denom}
              wrapperStyles={{ fontSize: 'xs' }}
              iconSize={4}
            />
          }
        />
      </SimpleGrid>
      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }}>
        <StatsRow
          name={t('common.tx-hash')}
          value={
            <CopiableLink
              trimmedLength={12}
              maxLength={32}
              link={`${EXPLORERS.INJECTIVE_TRANSACTIONS}${gameDetails.txHash}/`}
              value={gameDetails.txHash}
            />
          }
        />
        <StatsRow name={t('common.date')} value={<Text>{gameDetails.updated.toLocaleString()}</Text>} />
      </SimpleGrid>
      {gameDetails.mode === CoinflipGameMode.Instant ? (
        <CoinflipInstantResult round={gameDetails.rounds?.[0] as any} />
      ) : (
        <CoinflipMultipleResult
          rounds={gameDetails.rounds as any}
          gameSettings={{
            amount: formatAmount(gameDetails.bet, { decimals: token.decimals }) as string,
            denom: gameDetails.denom as string,
            maxGameRound: gameDetails.maxGameRound as number,
            maxWin: new BigNumber(gameDetails.maxWin),
            rtp: parseFloat(gameDetails.rtp),
          }}
        />
      )}
    </>
  )
}

type Round = { userChoice: CoinflipChoice; result: MiniGameStatus }

const CoinflipInstantResult = ({ round }: { round: Round }) => {
  const { t } = useTranslation()
  const isHead = round.userChoice === CoinflipChoice.Heads
  const isWin = round.result === MiniGameStatus.Win
  const boxShadow = isWin ? '0px -4px 43.7px 0px #68F27F inset' : '0px -4px 43.7px 0px #FF3288 inset'

  return (
    <SimpleGrid columns={2} gap={3}>
      <Box
        p={6}
        bg={'ninja.700'}
        rounded={'2xl'}
        boxShadow={isHead ? boxShadow : 'none'}
        opacity={!isHead ? 0.6 : 'none'}
      >
        <Icon mb={2} display={'block'} mx={'auto'} w={20} h={20} as={CoinHeadIcon} alt="" />
        <Text fontSize={'lg'} fontWeight={700} textAlign={'center'}>
          {t('coinflip.heads')}
        </Text>
      </Box>
      <Box
        p={6}
        bg={'ninja.700'}
        rounded={'2xl'}
        boxShadow={!isHead ? boxShadow : 'none'}
        opacity={isHead ? 0.6 : 'none'}
      >
        <Icon mb={2} display={'block'} mx={'auto'} w={20} h={20} as={CoinTailIcon} alt="" />
        <Text fontSize={'lg'} fontWeight={700} textAlign={'center'}>
          {t('coinflip.tails')}
        </Text>
      </Box>
    </SimpleGrid>
  )
}

type GameSettings = {
  maxGameRound: number
  rtp: number
  denom: string
  maxWin: BigNumber
  amount: string
}

const CoinflipMultipleResult = ({ rounds, gameSettings }: { rounds: Array<Round>; gameSettings: GameSettings }) => {
  const multipliers = calculateMultipliers(gameSettings)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const formatBalance = useFiatCurrencyStore((s) => s.formatBalance)
  const isUsdc = getTokenByDenom(gameSettings.denom)?.currency === SupportedCurrencies.USDC

  const formatPayable = (payable: string) => {
    const tm = new TokenMoney(payable, gameSettings.denom)
    if (isUsdc && !isUsd) return formatBalance(tm.humanNumber)
    return tm.humanFormat
  }

  return (
    <Flex rounded={'2xl'} bg={'ninja.700'} p={6} columnGap={1.5} rowGap={2} flexWrap={'wrap'}>
      {multipliers
        .slice(1)
        .map((multiplier, index) =>
          !multiplier.isLast ? (
            <StepBadge
              key={index}
              multiplier={multiplier.multiplier}
              coin={rounds?.[index]?.userChoice}
              isLost={rounds?.[index]?.result === MiniGameStatus.Lose}
            />
          ) : (
            <StepCoinBadge
              key={index}
              icon={isUsdc && !isUsd ? undefined : getTokenByDenom(gameSettings.denom).icon()}
              coin={rounds?.[index]?.userChoice}
              amount={formatPayable(multiplier.payable)}
              isLost={rounds?.[index]?.result === MiniGameStatus.Lose}
            />
          )
        )}
    </Flex>
  )
}

const transformCoinflipGameDetails = (data: Get_Coinflip_GameQuery) => {
  return {
    ...data.coinflipGame,
    updated: new Date(data.coinflipGame?.updated),
    txHash: data.coinflipGame?.txHash || '',
    rounds: data.coinflipGame?.coinflipGameRoundsByGameId.nodes,
  }
}
